import EntityMultiSelect from '../../../shared/components/EntityMultiSelect';
import { formSchema } from '../config/clientForm.schema';
import React, { useEffect, useRef, useState } from 'react';
import { Users } from '../../../shared/assets/AppIcons';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { PartyField, PartySelect, PartyFormSection, PARTY_LEGAL_FORMS } from '../../../shared/components/PartyFormFields';
import { CLIENT_TYPES } from '@/modules/clients/constants/client.constants';
import {
  getInitialCreateClientDialogState,
  validateCreateClientModel,
} from '@/modules/clients/components/createClientDialog.helpers';
import { toast } from '../../../shared/hooks/use-toast';
import ClientCustomFieldsSection from './ClientCustomFieldsSection';
import {
  CreateFormCancelButton,
  CreateFormDialog,
  CreateFormSubmitButton,
} from '../../../shared/components/CreateFormDialog';

function CreateClientDialog({
  open,
  onClose,
  onCreate,
  staff = [],
  staffError = '',
  staffCatalogStatus = 'empty',
  emptyClient,
  editing = false,
  comments = null,
}) {
  const initialState = getInitialCreateClientDialogState(emptyClient);
  const [model, setModel] = useState(initialState.model);
  const [referredByType, setReferredByType] = useState(() => editing && emptyClient?.referredBy && !emptyClient.referredBy.split('; ').every(name => staff.some(member => member.name === name)) ? 'altro' : initialState.referredByType);
  const [customReferral, setCustomReferral] = useState(editing ? emptyClient?.referredBy || '' : initialState.customReferral);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      const resetState = getInitialCreateClientDialogState(emptyClient);
      setModel(resetState.model);
      setReferredByType(resetState.referredByType);
      setCustomReferral(resetState.customReferral);
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  }, [open, emptyClient]);

  const save = async () => {
    if (submittingRef.current) return;

    const validationError = validateCreateClientModel(model);
    if (validationError) {
      toast({ title: 'Cliente non valido', description: validationError, variant: 'destructive' });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await onCreate(model);
      if (result?.ok === false) {
        toast({
          title: editing ? 'Cliente non aggiornato' : 'Cliente non creato',
          description: result.message || 'Impossibile creare il cliente.',
          variant: 'destructive',
        });
        return result;
      }

      toast({ title: editing ? 'Cliente aggiornato' : 'Cliente creato', description: result?.message || (editing ? 'Cliente modificato con successo!' : 'Cliente creato con successo!') });
      onClose();
      return result;
    } catch (error) {
      toast({
        title: 'Cliente non salvato',
        description: error.message || 'Impossibile salvare il cliente.',
        variant: 'destructive',
      });
      return { ok: false, message: error.message };
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const hasStaffOptions = staff.length > 0;
  const field = (name, id, props = {}) => <PartyField
    id={`client-${id}`} label={formSchema.field(name).label}
    value={model[name]} onChange={value => setModel(previous => ({ ...previous, [name]: value }))}
    disabled={isSubmitting} {...props} />;
  const contact = (group, type, label, placeholder) => <PartyField
    id={`client-${group === 'contattiPersonali' ? 'personal' : 'company'}-${type}`}
    label={label} type={type === 'email' ? 'email' : 'tel'} placeholder={placeholder}
    value={model[group]?.[type]} disabled={isSubmitting}
    onChange={value => setModel(previous => ({ ...previous, [group]: { ...previous[group], [type]: value } }))} />;

  return (
    <CreateFormDialog
      busy={isSubmitting}
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      icon={Users}
      title={editing ? "Modifica Cliente" : "Nuovo Cliente"}
      className="party-form-dialog"
      footer={
        <>
          <CreateFormCancelButton onClick={onClose} disabled={isSubmitting} />
          <CreateFormSubmitButton saving={isSubmitting} savingLabel={editing ? "Salvataggio..." : "Creazione..."} form="create-client-form">
            {editing ? "Salva modifiche" : "Crea Cliente"}
          </CreateFormSubmitButton>
        </>
      }
    >
      <form
        id="create-client-form"
        className="party-form create-form-dialog__form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <div className="party-form__row">
          {field('company', 'company', { required: true, placeholder: 'Esempio: ACME Corporation S.r.l.' })}
          {field('name', 'name', { label: 'Nome referente', required: true, placeholder: 'Esempio: Mario Rossi' })}
        </div>
        {field('notes', 'description', { label: 'Descrizione', multiline: true, rows: 3, maxLength: 4000, placeholder: 'Inserisci una descrizione...' })}
        <fieldset className="party-form__segments" disabled={isSubmitting}>
          <legend>Tipo cliente</legend>
          <div className="party-form__segment-options">
            {[[CLIENT_TYPES.CEDENTE, 'Cedente'], [CLIENT_TYPES.CESSIONARIO, 'Cessionario'], [CLIENT_TYPES.OTHER, 'Altro']].map(([value, label]) => <label key={value}>
              <input type="radio" name="client-type" value={value} checked={model.tipoCliente === value}
                onChange={() => setModel(previous => ({ ...previous, tipoCliente: value }))} />
              <span>{label}</span>
            </label>)}
          </div>
        </fieldset>
        <div className="party-form__row">
          {field('codiceFiscale', 'tax-code', { required: true, placeholder: '16 caratteri alfanumerici' })}
          {field('vatNumber', 'vat-number', { label: 'P.IVA', required: true, placeholder: '11 cifre numeriche' })}
        </div>
        <div className="party-form__row">
          {field('numeroREA', 'rea', { placeholder: 'Esempio: RM-123456' })}
          <PartySelect id="client-legal-form" label="Forma giuridica" value={model.formaGiuridica}
            onChange={formaGiuridica => setModel(previous => ({ ...previous, formaGiuridica }))}
            options={PARTY_LEGAL_FORMS} placeholder="Seleziona forma..." disabled={isSubmitting} />
        </div>
        <div className="party-form__row">
          {field('sector', 'sector', { placeholder: 'Esempio: Information Technology, Logistica...' })}
          {field('businessName', 'business-name', { required: true, placeholder: 'Esempio: Rossi Mario S.a.s.' })}
        </div>
        {field('indirizzoSedeLegale', 'registered-address', { placeholder: 'Via, Numero, CAP, Città, Provincia' })}
        <PartyFormSection title="Contatti">
          <div className="party-form__row">
            {contact('contattiPersonali', 'email', 'Email personale', 'mario.rossi@email.it')}
            {contact('contattiPersonali', 'phone', 'Telefono personale', '+39 347 1234567')}
          </div>
          <div className="party-form__row">
            {contact('contattiAziendali', 'email', 'Email aziendale', 'amministrazione@azienda.it')}
            {contact('contattiAziendali', 'phone', 'Telefono aziendale', '+39 02 123456')}
          </div>
        </PartyFormSection>
        <PartyFormSection title="Informazioni aggiuntive">
          <div className="party-form__field">
            <Label htmlFor="client-referred-by">Riferito da</Label>
            <EntityMultiSelect id="client-referred-by" disabled={isSubmitting} label="Riferito da"
              getLabel={member => member.role ? `${member.name} - ${member.role}` : member.name}
              options={staff.map(member => ({ ...member, id: member.name }))}
              value={referredByType === 'altro' ? [] : (model.referredBy || '').split('; ').filter(Boolean)}
              onChange={names => { setReferredByType('staff'); setCustomReferral(''); setModel(previous => ({ ...previous, referredBy: names.join('; ') })); }} />
            {!hasStaffOptions && <p role="status" className="party-form__help">{staffCatalogStatus === 'unavailable'
              ? staffError || 'Catalogo referral staff non disponibile. Puoi inserire un referral esterno manualmente.'
              : 'Nessun membro del personale disponibile. Puoi inserire un referral esterno manualmente.'}</p>}
            <button type="button" className="party-form__text-action" disabled={isSubmitting}
              onClick={() => { setReferredByType('altro'); setModel(previous => ({ ...previous, referredBy: customReferral })); }}>Altro (inserisci manualmente)</button>
            {referredByType === 'altro' && <Input id="client-custom-referral" aria-label="Referral esterno"
              placeholder="Nome e cognome del referral esterno..." value={customReferral} disabled={isSubmitting}
              onChange={event => { setCustomReferral(event.target.value); setModel(previous => ({ ...previous, referredBy: event.target.value })); }} />}
          </div>
        </PartyFormSection>

        <ClientCustomFieldsSection
          formKey="clients.client.create"
          values={model.customFields || {}}
          onChange={(customFields) => setModel({ ...model, customFields })}
        />

      </form>
      {comments}
    </CreateFormDialog>
  );
}

export default CreateClientDialog;
